import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useUpdateContractBillDetails } from '@/features/establishments/hooks/contract/useUpdateContractBillDetails'
import {
  UpdateContractBillDetailsSchema,
  updateContractBillDetailsSchema,
} from '@/features/establishments/schemas/contract/updateBillDetails.schema'
import { EstablishmentContractType } from '@/features/establishments/types/establishmentContract.type'
import { establishmentOptions as cycleOptions } from '@/features/privateCompanyLeadsFunnel/constants/cycleOptions.constant'
import { options as monthDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/monthDaysOptions.constant'
import { options as weekDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/weekDaysOptions.constants'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { InfoIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface UpdateBillDetailsModalProps {
  contract?: EstablishmentContractType
}

export const UpdateBillDetailsModal = ({ contract }: UpdateBillDetailsModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const getCycleStartDayDefaultValue = useMemo(() => {
    return contract?.cycle === 'weekly'
      ? contract?.cycle_start_day !== null && contract?.cycle_start_day !== undefined
        ? weekDaysOptions[contract?.cycle_start_day]
        : undefined
      : monthDaysOptions.find(({ value }) => value === `${contract?.cycle_start_day}`)
  }, [contract?.cycle])

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateContractBillDetailsSchema>({
    resolver: zodResolver(updateContractBillDetailsSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['establishmentContract', contract?.id] })
    setIsOpen(false)
  }

  const { mutate: updateContractBillDetails, isPending: updateContractBillDetailsIsPending } =
    useUpdateContractBillDetails({
      contractId: contract?.id,
      onSuccess,
      onError: FormTransformers.errorsTransformer<UpdateContractBillDetailsSchema>({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<UpdateContractBillDetailsSchema> = (data) => {
    updateContractBillDetails(data)
  }

  const watchCycle = watch('contract.cycle')

  useEffect(() => {
    if (isOpen) {
      reset({
        contract: {
          cycle: cycleOptions.find(({ value }) => value === contract?.cycle),
          cycle_start_day: getCycleStartDayDefaultValue,
          closing_to_due_in_days: contract?.closing_to_due_in_days
            ? {
                label: `D+${contract?.closing_to_due_in_days}`,
                value: `${contract?.closing_to_due_in_days}`,
              }
            : undefined,
        },
      })
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_bill_details_modal"
          operationKlassToCheckPermission={'Admin::Client::Contracts::UpdateContractBillInformation'}
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando detalhes da fatura"
      dataTestid="update_bill_details"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateContractBillDetailsIsPending}
      confirmButtonIsLoading={updateContractBillDetailsIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="contract.cycle"
              label="Ciclo de fatura"
              options={cycleOptions}
              triggerOnChange={({ value }) => {
                setValue('contract.cycle_start_day', value === 'weekly' ? weekDaysOptions[0] : monthDaysOptions[0])
              }}
              isDisabled={updateContractBillDetailsIsPending}
              errorMessage={errors?.contract?.cycle?.message}
            />

            <div className="flex gap-x-2">
              <SelectController
                control={control}
                name="contract.cycle_start_day"
                label="Dia de abertura"
                className="w-full"
                options={watchCycle?.value === 'weekly' ? weekDaysOptions : monthDaysOptions(28)}
                isDisabled={updateContractBillDetailsIsPending}
                errorMessage={errors?.contract?.cycle_start_day?.message}
              />

              <SelectController
                control={control}
                name="contract.closing_to_due_in_days"
                label="Vencimento da fatura"
                className="w-full"
                options={monthDaysOptions(30).map((day) => ({ label: `D+${day.label}`, value: day.value }))}
                isDisabled={updateContractBillDetailsIsPending}
                errorMessage={errors?.contract?.closing_to_due_in_days?.message}
              />
            </div>

            <div className="flex items-center gap-x-2">
              <InfoIcon strokeWidth={1} className="size-6 text-warning" />

              <p className="text-[0.625rem] leading-tight font-light italic">
                Estas mudanças so podem ser feitas dentro de uma janela de manutenção, das 8 da manha ate as 8 da noite
              </p>
            </div>

            <div className="flex items-center gap-x-2">
              <InfoIcon strokeWidth={1} className="size-6 text-warning" />

              <p className="text-[0.625rem] leading-tight font-light italic">
                Estas mudanças so terão efeito após o fechamento da próxima fatura.
              </p>
            </div>
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
